import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import celebrityService from '../../services';
import GlassCard from '../ui/GlassCard';
import './CelebrityProfile.css';

const CelebrityProfile = () => {
  const { pbId } = useParams();
  const navigate = useNavigate();
  const [celebrity, setCelebrity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showDealPopup, setShowDealPopup] = useState(false);
  
  // Helper function to get company ID from name
  const getCompanyIdFromName = (companyName) => {
    // First check if we have company_id in the investment data
    if (celebrity && celebrity.investments) {
      const investment = celebrity.investments.find(inv => inv.company_name === companyName);
      if (investment && investment.company_id) {
        return investment.company_id;
      }
    }
    
    // Check recent deals too
    if (celebrity && celebrity.recent_deals) {
      const deal = celebrity.recent_deals.find(d => d.company_name === companyName);
      if (deal && deal.company_id) {
        return deal.company_id;
      }
    }
    
    // Map of known company names to IDs (for mock data)
    const companyMap = {
      'Clay': 'clay-001',
      'Uber': 'uber-002',
      'Spotify': 'spotify-003',
      'Airbnb': 'airbnb-004',
      'Goop': 'goop-005',
      'OpenAI': 'openai-006'
    };
    
    // Return mapped ID or create a slug from the name
    return companyMap[companyName] || companyName.toLowerCase().replace(/\s+/g, '-') + '-' + Math.random().toString(36).substr(2, 3);
  };

  useEffect(() => {
    const fetchCelebrityData = async () => {
      try {
        setLoading(true);
        const celebrityData = await celebrityService.getCelebrityDetails(pbId);
        setCelebrity(celebrityData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCelebrityData();
  }, [pbId]);

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    if (amount >= 1000000000) return `$${(amount / 1000000000).toFixed(1)}B`;
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(0)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const handleDealClick = (deal) => {
    setSelectedDeal(deal);
    setShowDealPopup(true);
  };

  if (loading) {
    return (
      <div className="celebrity-profile loading">
        <div className="loading-spinner"></div>
        <p>Loading celebrity profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="celebrity-profile error">
        <p>Error loading celebrity: {error}</p>
        <button onClick={() => navigate('/celebrities')}>Back to Celebrities</button>
      </div>
    );
  }

  if (!celebrity) {
    return (
      <div className="celebrity-profile not-found">
        <p>Celebrity not found</p>
        <button onClick={() => navigate('/celebrities')}>Back to Celebrities</button>
      </div>
    );
  }

  return (
    <div className="celebrity-profile">
      <div className="profile-header">
        <button className="back-button" onClick={() => navigate('/celebrities')}>
          ← Back to Celebrities
        </button>
      </div>

      {/* Header Section */}
      <div className="profile-header-section">
        <h1 className="celebrity-name">{celebrity.investor_name}</h1>
        <p className="celebrity-title">{celebrity.display_role}</p>
        
        <div className="header-metrics">
          <div className="metric">
            <span className="metric-label">Total Investments</span>
            <span className="metric-value">{celebrity.total_investments}</span>
          </div>
          <div className="metric">
            <span className="metric-label">Total $ Active</span>
            <span className="metric-value">{formatCurrency(celebrity.total_invested_active)}</span>
          </div>
          <div className="metric">
            <span className="metric-label">Total $ Previous</span>
            <span className="metric-value">{formatCurrency(celebrity.total_invested_previous)}</span>
          </div>
          <div className="metric">
            <span className="metric-label">Active Portfolio</span>
            <span className="metric-value">{celebrity.active_portfolio}</span>
          </div>
          <div className="metric">
            <span className="metric-label">Exits</span>
            <span className="metric-value">{celebrity.total_exits}</span>
          </div>
          <div className="metric">
            <span className="metric-label">Median Round</span>
            <span className="metric-value">{formatCurrency(celebrity.median_round_amount)}</span>
          </div>
          <div className="metric">
            <span className="metric-label">Median Valuation</span>
            <span className="metric-value">{formatCurrency(celebrity.median_valuation)}</span>
          </div>
          <div className="metric">
            <span className="metric-label">Last Investment</span>
            <span className="metric-value">{celebrity.latest_investment_timeago}</span>
          </div>
        </div>
        
        <div className="preferred-industries">
          <span className="industries-label">Preferred Industries:</span>
          {celebrity.preferred_industries?.map((industry, idx) => (
            <span key={idx} className="industry-tag">{industry}</span>
          ))}
        </div>
      </div>

      {/* 5 Recent Deals Section */}
      <section className="recent-deals-section">
        <h2 className="section-title">5 Recent Deals</h2>
        <div className="recent-deals-grid">
          {celebrity.recent_deals?.slice(0, 5).map((deal) => (
            <GlassCard
              key={deal.deal_id}
              className="deal-card"
            >
              <div className="deal-header">
                <h3 
                  className="deal-company clickable"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/company/${deal.company_id || getCompanyIdFromName(deal.company_name)}`);
                  }}
                >
                  {deal.company_name}
                </h3>
                <span 
                  className="deal-number clickable"
                  onClick={() => handleDealClick(deal)}
                >
                  #{deal.deal_number}
                </span>
              </div>
              <div className="deal-details" onClick={() => handleDealClick(deal)}>
                <div className="deal-info">
                  <span className="deal-round">{deal.round_type}</span>
                  <span className="deal-amount">{formatCurrency(deal.total_round_size)}</span>
                </div>
                <div className="deal-meta">
                  <span className="deal-industry">{deal.industry}</span>
                  <span className="deal-location">{deal.location}</span>
                  <span className="deal-date">{formatDate(deal.deal_date)}</span>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      </section>

      {/* Recent Investments & M&A Table */}
      <section className="investments-section">
        <h2 className="section-title">Recent Investments & M&A ({celebrity.investments?.length || 0})</h2>
        <div className="table-container">
          <table className="investments-table">
            <thead>
              <tr>
                <th>Company Name</th>
                <th>Deal Date</th>
                <th>Deal Type</th>
                <th>Deal Size</th>
                <th>Co-Investors</th>
                <th>Company Stage</th>
                <th>Industry</th>
                <th>Lead Partner</th>
              </tr>
            </thead>
            <tbody>
              {celebrity.investments?.map((investment, idx) => (
                <tr key={idx}>
                  <td 
                    className="company-name clickable"
                    onClick={() => navigate(`/company/${investment.company_id || getCompanyIdFromName(investment.company_name)}`)}
                  >
                    {investment.company_name}
                  </td>
                  <td>{investment.investment_date}</td>
                  <td>{investment.round_type || 'Unknown'}</td>
                  <td>{formatCurrency(investment.initial_investment)}</td>
                  <td className="co-investors-count">{investment.co_investors_count || investment.other_investors?.length || 0}</td>
                  <td>Generating Revenue</td>
                  <td>{investment.industry}</td>
                  <td>{investment.lead_partner || celebrity.investor_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Exits Table */}
      {celebrity.exits && celebrity.exits.length > 0 && (
        <section className="exits-section">
          <h2 className="section-title">Exits ({celebrity.exits.length})</h2>
          <div className="table-container">
            <table className="exits-table">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Exit Date</th>
                  <th>Exit Type</th>
                  <th>Exit Size</th>
                  <th>Status</th>
                  <th>Buyers</th>
                </tr>
              </thead>
              <tbody>
                {celebrity.exits.map((exit, idx) => (
                  <tr key={idx}>
                    <td 
                      className="company-name clickable"
                      onClick={() => {
                        // Try to find company_id from investments
                        const investment = celebrity.investments?.find(inv => inv.company_name === exit.company_name);
                        const companyId = investment?.company_id || getCompanyIdFromName(exit.company_name);
                        navigate(`/company/${companyId}`);
                      }}
                    >
                      {exit.company_name}
                    </td>
                    <td>{exit.exit_date}</td>
                    <td>{exit.exit_type}</td>
                    <td>{exit.exit_value ? formatCurrency(exit.exit_value) : ''}</td>
                    <td>Completed</td>
                    <td>{exit.acquirer || exit.buyers || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Co-Investors Table */}
      {celebrity.co_investors && celebrity.co_investors.length > 0 && (
        <section className="co-investors-section">
          <h2 className="section-title">Co-Investors ({celebrity.co_investors.length})</h2>
          <div className="table-container">
            <table className="co-investors-table">
              <thead>
                <tr>
                  <th>Co-Investors</th>
                  <th>Before</th>
                  <th>With<br/>{celebrity.investor_name}</th>
                  <th>After</th>
                  <th>Annual Activity<br/>'21 - '25</th>
                  <th>Series</th>
                  <th>Exits</th>
                  <th>Lead<br/>Partner</th>
                  <th>Industry</th>
                </tr>
              </thead>
              <tbody>
                {celebrity.co_investors.map((coInvestor, idx) => (
                  <tr key={idx}>
                    <td className="investor-name">{coInvestor.name}</td>
                    <td className="centered">{coInvestor.before || ''}</td>
                    <td className="centered">{coInvestor.with || coInvestor.shared_deals || 0}</td>
                    <td className="centered">{coInvestor.after || ''}</td>
                    <td className="activity">{coInvestor.annual_activity || "'21-'25"}</td>
                    <td className="centered">{coInvestor.series || ''}</td>
                    <td className="centered">{coInvestor.exits || ''}</td>
                    <td className="centered">{coInvestor.lead_partner || coInvestor.shared_deals || 0}</td>
                    <td>{coInvestor.industry || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Deal Popup */}
      {showDealPopup && selectedDeal && (
        <DealPopup
          deal={selectedDeal}
          onClose={() => {
            setShowDealPopup(false);
            setSelectedDeal(null);
          }}
        />
      )}
    </div>
  );
};

// Deal Popup Component
const DealPopup = ({ deal, onClose }) => {
  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    if (amount >= 1000000000) return `$${(amount / 1000000000).toFixed(2)}B`;
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount}`;
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="deal-popup" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        
        <div className="popup-header">
          <h2>Deal #{deal.deal_number}: {deal.round_type}, {formatCurrency(deal.total_round_size)}; {new Date(deal.deal_date).toLocaleDateString()}</h2>
        </div>

        <div className="popup-content">
          {/* Deal Synopsis */}
          <section className="popup-section">
            <h3>Deal Synopsis</h3>
            <p className="synopsis">{deal.deal_synopsis}</p>
          </section>

          {/* Deal Info */}
          <section className="popup-section">
            <h3>Deal Info</h3>
            <table className="info-table">
              <tbody>
                <tr>
                  <td className="label">Deal Types</td>
                  <td>{deal.round_type}</td>
                </tr>
                <tr>
                  <td className="label">Deal Date</td>
                  <td>{new Date(deal.deal_date).toLocaleDateString()}</td>
                </tr>
                <tr>
                  <td className="label">Deal Status</td>
                  <td>{deal.deal_status}</td>
                </tr>
                <tr>
                  <td className="label">Financing Source</td>
                  <td>{deal.financing_source}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Company Info */}
          <section className="popup-section">
            <h3>Company Info</h3>
            <table className="info-table">
              <tbody>
                <tr>
                  <td className="label">Business Status</td>
                  <td>{deal.company_status}</td>
                </tr>
                <tr>
                  <td className="label">CEO/Lead MGT</td>
                  <td>{deal.ceo}</td>
                </tr>
                <tr>
                  <td className="label">Industry</td>
                  <td>{deal.industry}</td>
                </tr>
                <tr>
                  <td className="label">Location</td>
                  <td>{deal.location}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Total Capital */}
          <section className="popup-section">
            <h3>Total Capital</h3>
            <table className="info-table">
              <tbody>
                <tr>
                  <td className="label">Deal Amount</td>
                  <td>{formatCurrency(deal.total_round_size)}</td>
                </tr>
                <tr>
                  <td className="label">Post-Money Valuation</td>
                  <td>{formatCurrency(deal.post_money_valuation)}</td>
                </tr>
                <tr>
                  <td className="label">Investor Amount</td>
                  <td>{formatCurrency(deal.amount_invested)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Equity */}
          <section className="popup-section">
            <h3>Equity</h3>
            <table className="info-table">
              <tbody>
                <tr>
                  <td className="label">VC Round</td>
                  <td>{deal.vc_round || '1st Round'}</td>
                </tr>
                <tr>
                  <td className="label">Total Invested Equity</td>
                  <td>{formatCurrency(deal.total_round_size)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Investors */}
          {deal.investors && (
            <section className="popup-section">
              <h3>Investors</h3>
              <table className="investors-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Investor Name</th>
                    <th>Status</th>
                    <th>Lead/Sole</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {deal.investors.map((investor, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{investor.name}</td>
                      <td>{investor.status || 'New Investor'}</td>
                      <td>{investor.lead ? 'Yes' : 'No'}</td>
                      <td>
                        <strong>Lead Partner:</strong> {investor.lead_partner || investor.name}<br/>
                        <strong>Form of Payment:</strong> {investor.payment_form || 'Cash'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Advisors */}
          {deal.advisors && (
            <section className="popup-section">
              <h3>Advisors</h3>
              <table className="advisors-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Advisor Name</th>
                    <th>Service Type</th>
                    <th>Hired By</th>
                  </tr>
                </thead>
                <tbody>
                  {deal.advisors.map((advisor, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{advisor.name}</td>
                      <td>{advisor.service_type}</td>
                      <td>{advisor.hired_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Tranches */}
          {deal.tranches && (
            <section className="popup-section">
              <h3>Tranches</h3>
              <table className="tranches-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Tranche Date</th>
                    <th>Amount</th>
                    <th>Financing Type</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {deal.tranches.map((tranche, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{new Date(tranche.date).toLocaleDateString()}</td>
                      <td>{formatCurrency(tranche.amount)}</td>
                      <td>{tranche.financing_type}</td>
                      <td>
                        <strong>Investors:</strong> {tranche.investors?.join(', ')}<br/>
                        <strong>Tranche Size Status:</strong> {tranche.status || 'Actual'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Financials */}
          {deal.financials && (
            <section className="popup-section">
              <h3>Financials (TTM Dec 2023)</h3>
              <p className="popup-subtitle" style={{fontSize: '0.875rem', color: '#8B8B8B', marginBottom: '1rem'}}>
                All Financials in Thousands, USD
              </p>
              
              {/* Deal Multiples */}
              <h4 style={{fontSize: '1.125rem', marginTop: '1.5rem', marginBottom: '1rem', color: '#ffffff'}}>Deal Multiples</h4>
              <table className="info-table">
                <tbody>
                  <tr>
                    <td className="label">Deal Size / EBITDA</td>
                    <td>{deal.financials.deal_ebitda_multiple || '-3.02'}</td>
                  </tr>
                  <tr>
                    <td className="label">Deal Size / EBIT</td>
                    <td>{deal.financials.deal_ebit_multiple || '-3.01'}</td>
                  </tr>
                  <tr>
                    <td className="label">Deal Size / Net Income</td>
                    <td>{deal.financials.deal_net_income_multiple || '-2.89'}</td>
                  </tr>
                  <tr>
                    <td className="label">Deal Size / Revenue</td>
                    <td>{deal.financials.deal_revenue_multiple || '690.20'}</td>
                  </tr>
                </tbody>
              </table>

              {/* Income Statement */}
              <h4 style={{fontSize: '1.125rem', marginTop: '1.5rem', marginBottom: '1rem', color: '#ffffff'}}>Income Statement</h4>
              <table className="info-table">
                <tbody>
                  <tr>
                    <td className="label">Total Revenue</td>
                    <td>{formatCurrency(deal.financials.revenue_ttm || 22000)}</td>
                  </tr>
                  <tr>
                    <td className="label">Total Operating Profit/(Loss)</td>
                    <td className={deal.financials.operating_profit < 0 ? 'negative' : ''}>
                      ({formatCurrency(Math.abs(deal.financials.operating_profit || 4977000))})
                    </td>
                  </tr>
                  <tr>
                    <td className="label">EBITDA</td>
                    <td className={deal.financials.ebitda < 0 ? 'negative' : ''}>
                      ({formatCurrency(Math.abs(deal.financials.ebitda || 4968000))})
                    </td>
                  </tr>
                  <tr>
                    <td className="label">EBIT</td>
                    <td className={deal.financials.ebit < 0 ? 'negative' : ''}>
                      ({formatCurrency(Math.abs(deal.financials.ebit || 4977000))})
                    </td>
                  </tr>
                  <tr>
                    <td className="label">Net Income Available to Common</td>
                    <td className={deal.financials.net_income < 0 ? 'negative' : ''}>
                      ({formatCurrency(Math.abs(deal.financials.net_income || 5199000))})
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Balance Sheet */}
              <h4 style={{fontSize: '1.125rem', marginTop: '1.5rem', marginBottom: '1rem', color: '#ffffff'}}>Balance Sheet</h4>
              <table className="info-table">
                <tbody>
                  <tr>
                    <td className="label">Total Current Assets</td>
                    <td>{formatCurrency(deal.financials.current_assets || 4726000)}</td>
                  </tr>
                  <tr>
                    <td className="label">Net Property, Plant and Equipment</td>
                    <td>{formatCurrency(deal.financials.ppe || 15000)}</td>
                  </tr>
                  <tr>
                    <td className="label">Total Non-Current Assets</td>
                    <td>{formatCurrency(deal.financials.non_current_assets || 1480000)}</td>
                  </tr>
                  <tr>
                    <td className="label">Total Assets</td>
                    <td>{formatCurrency(deal.financials.total_assets || 6205000)}</td>
                  </tr>
                  <tr>
                    <td className="label">Total Current Liabilities</td>
                    <td>{formatCurrency(deal.financials.current_liabilities || 6200000)}</td>
                  </tr>
                  <tr>
                    <td className="label">Total Liabilities</td>
                    <td>{formatCurrency(deal.financials.total_liabilities || 6200000)}</td>
                  </tr>
                  <tr>
                    <td className="label">Total Equity</td>
                    <td>{formatCurrency(deal.financials.total_equity || 5000)}</td>
                  </tr>
                  <tr>
                    <td className="label">Working Capital</td>
                    <td className={deal.financials.working_capital < 0 ? 'negative' : ''}>
                      ({formatCurrency(Math.abs(deal.financials.working_capital || 1475000))})
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Ratios */}
              <h4 style={{fontSize: '1.125rem', marginTop: '1.5rem', marginBottom: '1rem', color: '#ffffff'}}>Ratios</h4>
              <table className="info-table">
                <tbody>
                  <tr>
                    <td className="label">EBITDA Margin</td>
                    <td>{deal.financials.ebitda_margin || '-22,859.74'}%</td>
                  </tr>
                  <tr>
                    <td className="label">Revenue % Growth</td>
                    <td>{deal.financials.growth_rate || '-88.01'}%</td>
                  </tr>
                  <tr>
                    <td className="label">Current Ratio</td>
                    <td>{deal.financials.current_ratio || '0.76'}</td>
                  </tr>
                  <tr>
                    <td className="label">Quick Ratio</td>
                    <td>{deal.financials.quick_ratio || '0.49'}</td>
                  </tr>
                  <tr>
                    <td className="label">Total Asset Turnover</td>
                    <td>{deal.financials.asset_turnover || '0.01'}</td>
                  </tr>
                </tbody>
              </table>

              {/* Data Origination */}
              <h4 style={{fontSize: '1.125rem', marginTop: '1.5rem', marginBottom: '1rem', color: '#ffffff'}}>Data Origination</h4>
              <table className="info-table">
                <tbody>
                  <tr>
                    <td className="label">Original</td>
                    <td>Yes</td>
                  </tr>
                  <tr>
                    <td className="label">Calculated</td>
                    <td>Yes</td>
                  </tr>
                </tbody>
              </table>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default CelebrityProfile;